<template>
    <transition name="modal-slide">
        <div v-if="isdisplay" class="modal-overlay" @click.self="close">
            <div class="modal-container">
                <!-- Header (gabarit charte cf-modal-head) -->
                <header class="cf-modal-head cf-dialog-head">
                    <div class="cf-modal-head__main">
                        <span class="cf-modal-head__icon">
                            <i :class="typeCode === 'barcode' ? 'ri-barcode-line' : 'ri-qr-code-line'"></i>
                        </span>
                        <div>
                            <h2 class="cf-dialog-title">Générateur de {{ typeCode === 'barcode' ? 'codes-barres' : 'QR codes' }}</h2>
                            <p class="cf-modal-head__sub">{{ namecode }}</p>
                        </div>
                    </div>
                    <button @click="close" type="button" class="cf-dialog-close" aria-label="Fermer">
                        <i class="ri-close-line"></i>
                    </button>
                </header>

                <!-- Type Selector (Barcode / QR Code) -->
                <div class="type-selector">
                    <button
                            @click="typeCode = 'barcode'"
                            :class="['type-btn', { active: typeCode === 'barcode' }]"
                    >
                        <div class="type-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M3 5h2v14H3zM7 5h1v14H7zM11 5h2v14h-2zM15 5h1v14h-1zM19 5h2v14h-2z"/>
                            </svg>
                        </div>
                        <div class="type-info">
                            <span class="type-name">Code-barres</span>
                            <span class="type-desc">Format Code128</span>
                        </div>
                        <div v-if="typeCode === 'barcode'" class="type-check">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                        </div>
                    </button>

                    <button
                            @click="typeCode = 'qrcode'"
                            :class="['type-btn', { active: typeCode === 'qrcode' }]"
                    >
                        <div class="type-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="3" width="7" height="7"/>
                                <rect x="14" y="3" width="7" height="7"/>
                                <rect x="3" y="14" width="7" height="7"/>
                                <rect x="14" y="14" width="4" height="4"/>
                                <line x1="21" y1="14" x2="21" y2="21"/>
                                <line x1="14" y1="21" x2="21" y2="21"/>
                            </svg>
                        </div>
                        <div class="type-info">
                            <span class="type-name">QR Code</span>
                            <span class="type-desc">Scannable 2D</span>
                        </div>
                        <div v-if="typeCode === 'qrcode'" class="type-check">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                        </div>
                    </button>
                </div>

                <!-- Layout Selector -->
                <div class="layout-section">
                    <div class="layout-header">
                        <div class="layout-header-top">
                            <h3 class="layout-title">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <rect x="3" y="3" width="7" height="7"/>
                                    <rect x="14" y="3" width="7" height="7"/>
                                    <rect x="14" y="14" width="7" height="7"/>
                                    <rect x="3" y="14" width="7" height="7"/>
                                </svg>
                                Format d'étiquettes
                            </h3>

                            <!-- Toggle Afficher/Masquer -->
                            <div class="layout-toggle-wrapper">
                                <button
                                        @click="toggleLayoutSection"
                                        :class="['layout-toggle-btn', { active: layoutSectionVisible }]"
                                        type="button"
                                >
                                    <span class="toggle-track">
                                        <span class="toggle-thumb"></span>
                                    </span>
                                    <span class="toggle-label">{{ layoutSectionVisible ? 'Masquer' : 'Afficher' }}</span>
                                </button>
                            </div>
                        </div>

                        <span class="layout-selected" v-if="layoutSectionVisible">{{ selectedLayout.name }}</span>
                    </div>

                    <!-- Contenu avec transition -->
                    <transition name="layout-slide">
                        <div v-if="layoutSectionVisible" class="layout-content">
                            <div class="layout-categories">
                                <button
                                        v-for="category in layoutCategories"
                                        :key="category.id"
                                        @click="activeCategory = category.id"
                                        :class="['category-btn', { active: activeCategory === category.id }]"
                                >
                                    <span class="category-icon">{{ category.icon }}</span>
                                    <span class="category-name">{{ category.name }}</span>
                                </button>
                            </div>

                            <div class="layout-grid">
                                <button
                                        v-for="layout in filteredLayouts"
                                        :key="layout.id"
                                        @click="selectLayout(layout)"
                                        :class="['layout-card', { active: selectedLayoutId === layout.id }]"
                                >
                                    <div class="layout-preview" :style="getLayoutPreviewStyle(layout)">
                                        <div
                                                v-for="n in Math.min(layout.cols * layout.rows, 12)"
                                                :key="n"
                                                class="preview-cell"
                                                :style="getPreviewCellStyle(layout)"
                                        ></div>
                                    </div>
                                    <div class="layout-info">
                                        <span class="layout-name">{{ layout.name }}</span>
                                        <span class="layout-dimensions">{{ layout.cols }}×{{ layout.rows }} • {{ layout.paperSize }}</span>
                                    </div>
                                    <div v-if="selectedLayoutId === layout.id" class="layout-check">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                            <polyline points="20 6 9 17 4 12"/>
                                        </svg>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </transition>
                </div>

                <!-- Configuration Panel -->
                <div class="config-panel">
                    <div class="config-header" @click="configExpanded = !configExpanded">
                        <h3 class="config-title">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="3"/>
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                            </svg>
                            Paramètres avancés
                        </h3>
                        <button class="config-toggle" :class="{ expanded: configExpanded }">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="6 9 12 15 18 9"/>
                            </svg>
                        </button>
                    </div>

                    <transition name="config-slide">
                        <div v-show="configExpanded" class="config-content">
                            <div class="config-grid">
                                <!-- Orientation -->
                                <div class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                        </svg>
                                        Orientation
                                    </label>
                                    <div class="toggle-group">
                                        <button
                                                @click="taillemodel = 'A4'"
                                                :class="['toggle-btn', { active: taillemodel === 'A4' }]"
                                        >
                                            <svg class="orientation-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <rect x="6" y="3" width="12" height="18" rx="1"/>
                                            </svg>
                                            <span class="toggle-text">Portrait</span>
                                        </button>
                                        <button
                                                @click="taillemodel = 'A4landscape'"
                                                :class="['toggle-btn', { active: taillemodel === 'A4landscape' }]"
                                        >
                                            <svg class="orientation-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <rect x="3" y="6" width="18" height="12" rx="1"/>
                                            </svg>
                                            <span class="toggle-text">Paysage</span>
                                        </button>
                                    </div>
                                </div>

                                <!-- Nombre d'exemplaires -->
                                <div class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <rect x="6" y="4" width="4" height="16"/>
                                            <rect x="14" y="4" width="4" height="16"/>
                                        </svg>
                                        Quantité
                                    </label>
                                    <div class="number-input-wrapper">
                                        <button @click="decrementQuantity" class="number-btn" :disabled="nombreimprimable <= 1">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <line x1="5" y1="12" x2="19" y2="12"/>
                                            </svg>
                                        </button>
                                        <input
                                                type="number"
                                                v-model.number="nombreimprimable"
                                                class="number-input"
                                                min="1"
                                                max="500"
                                                @change="validateQuantity"
                                        >
                                        <button @click="incrementQuantity" class="number-btn" :disabled="nombreimprimable >= 500">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <line x1="12" y1="5" x2="12" y2="19"/>
                                                <line x1="5" y1="12" x2="19" y2="12"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <!-- Taille du titre -->
                                <div class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <polyline points="4 7 4 4 20 4 20 7"/>
                                            <line x1="9" y1="20" x2="15" y2="20"/>
                                            <line x1="12" y1="4" x2="12" y2="20"/>
                                        </svg>
                                        Taille texte
                                    </label>
                                    <div class="slider-wrapper">
                                        <input
                                                type="range"
                                                v-model.number="titletaille"
                                                class="slider-input"
                                                min="6"
                                                max="24"
                                        >
                                        <span class="slider-value">{{ titletaille }}px</span>
                                    </div>
                                </div>

                                <!-- Afficher le prix -->
                                <div class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <line x1="12" y1="1" x2="12" y2="23"/>
                                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                                        </svg>
                                        Afficher prix
                                    </label>
                                    <div class="toggle-switch-wrapper">
                                        <button
                                                @click="showPrice = !showPrice"
                                                :class="['toggle-switch', { active: showPrice }]"
                                        >
                                            <span class="switch-track">
                                                <span class="switch-thumb"></span>
                                            </span>
                                            <span class="switch-label">{{ showPrice ? 'Oui' : 'Non' }}</span>
                                        </button>
                                    </div>
                                </div>

                                <!-- Prix (si activé) -->
                                <div v-if="showPrice" class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <path d="M12 6v12M9 9h6"/>
                                        </svg>
                                        Prix unitaire
                                    </label>
                                    <div class="price-input-wrapper">
                                        <input
                                                type="number"
                                                v-model.number="priceValue"
                                                class="price-input"
                                                min="0"
                                                step="0.01"
                                                placeholder="0.00"
                                                @change="validatePrice"
                                        >
                                        <span class="price-currency">{{ currency }}</span>
                                    </div>
                                </div>

                                <!-- Devise -->
                                <div v-if="showPrice" class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <path d="M12 6v12"/>
                                        </svg>
                                        Devise
                                    </label>
                                    <div class="select-wrapper">
                                        <select v-model="currency" class="config-select">
                                            <option value="FCFA">FCFA</option>
                                            <option value="€">Euro (€)</option>
                                            <option value="$">Dollar ($)</option>
                                            <option value="£">Livre (£)</option>
                                            <option value="MAD">Dirham (MAD)</option>
                                            <option value="XOF">XOF</option>
                                            <option value="XAF">XAF</option>
                                        </select>
                                    </div>
                                </div>

                                <!-- Taille QR Code (uniquement pour QR) -->
                                <div v-if="typeCode === 'qrcode'" class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <rect x="3" y="3" width="18" height="18" rx="2"/>
                                            <path d="M9 9h6v6H9z"/>
                                        </svg>
                                        Taille QR
                                    </label>
                                    <div class="slider-wrapper">
                                        <input
                                                type="range"
                                                v-model.number="qrSize"
                                                class="slider-input"
                                                min="40"
                                                max="200"
                                        >
                                        <span class="slider-value">{{ qrSize }}px</span>
                                    </div>
                                </div>

                                <!-- Couleur QR Code -->
                                <div v-if="typeCode === 'qrcode'" class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <circle cx="12" cy="12" r="3"/>
                                        </svg>
                                        Couleur QR
                                    </label>
                                    <div class="color-picker-wrapper">
                                        <input type="color" v-model="qrColor" class="color-input">
                                        <span class="color-value">{{ qrColor }}</span>
                                    </div>
                                </div>

                                <!-- Bordure étiquette -->
                                <div class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                        </svg>
                                        Bordure
                                    </label>
                                    <div class="toggle-switch-wrapper">
                                        <button
                                                @click="showBorder = !showBorder"
                                                :class="['toggle-switch', { active: showBorder }]"
                                        >
                                            <span class="switch-track">
                                                <span class="switch-thumb"></span>
                                            </span>
                                            <span class="switch-label">{{ showBorder ? 'Oui' : 'Non' }}</span>
                                        </button>
                                    </div>
                                </div>

                                <!-- Marges -->
                                <div class="config-item">
                                    <label class="config-label">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M21 3H3v18h18V3zM9 3v18M15 3v18M3 9h18M3 15h18"/>
                                        </svg>
                                        Marges (mm)
                                    </label>
                                    <div class="slider-wrapper">
                                        <input
                                                type="range"
                                                v-model.number="marginSize"
                                                class="slider-input"
                                                min="0"
                                                max="10"
                                        >
                                        <span class="slider-value">{{ marginSize }}mm</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>

                <!-- Preview Section -->
                <main class="modal-body">
                    <div class="preview-header">
                        <h3 class="preview-title">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                            </svg>
                            Aperçu ({{ skus.length }} étiquette{{ skus.length > 1 ? 's' : '' }})
                        </h3>
                        <div class="preview-actions">
                            <div class="preview-badge">
                                <span class="badge-text">{{ codebarre }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="preview-container" ref="printcodebarrep" id="printcodebarrep">
                        <div
                                class="code-grid"
                                ref="printcodebarre"
                                :style="getGridStyle()"
                        >
                            <div
                                    v-for="(item, key) in skus"
                                    :key="key"
                                    class="code-card"
                                    :class="[selectedLayout.cardClass, { 'with-border': showBorder }]"
                                    :style="getCardStyle()"
                            >
                                <div class="code-content">
                                    <!-- Nom produit -->
                                    <p
                                            class="code-label"
                                            :style="{ fontSize: titletaille + 'px' }"
                                    >
                                        {{ item.value }}
                                    </p>

                                    <!-- Code-barres -->
                                    <template v-if="typeCode === 'barcode'">
                                        <img
                                                :alt="item.title"
                                                :src="getBarcodeUrl(item.title)"
                                                class="barcode-image"
                                                :style="getBarcodeStyle()"
                                                @error="handleBarcodeError"
                                        />
                                    </template>

                                    <!-- QR Code -->
                                    <template v-else>
                                        <img
                                                v-if="qrCodeDataUrl"
                                                :alt="item.title"
                                                :src="qrCodeDataUrl"
                                                class="qr-image"
                                                :style="getQrCodeStyle()"
                                                @error="handleQrCodeError"
                                        />
                                        <div v-else class="qr-placeholder">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                                                <rect x="3" y="3" width="7" height="7"/>
                                                <rect x="14" y="3" width="7" height="7"/>
                                                <rect x="3" y="14" width="7" height="7"/>
                                            </svg>
                                        </div>
                                    </template>

                                    <!-- Code numéro -->
                                    <p class="code-number">{{ item.title }}</p>

                                    <!-- Prix -->
                                    <p v-if="showPrice && priceValue > 0" class="code-price">
                                        {{ formatPrice(priceValue) }} {{ currency }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div v-if="skus.length === 0" class="empty-state">
                            <div class="empty-icon">
                                <svg v-if="typeCode === 'barcode'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <path d="M3 5h2v14H3zM7 5h1v14H7zM11 5h2v14h-2zM15 5h1v14h-1zM19 5h2v14h-2z"/>
                                </svg>
                                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                    <rect x="3" y="3" width="7" height="7"/>
                                    <rect x="14" y="3" width="7" height="7"/>
                                    <rect x="3" y="14" width="7" height="7"/>
                                </svg>
                            </div>
                            <p class="empty-text">Ajustez la quantité pour générer les étiquettes</p>
                        </div>
                    </div>
                </main>

                <!-- Footer (gabarit charte) -->
                <footer class="cf-dialog-foot">
                    <button @click="close" type="button" class="ti-btn ti-btn-secondary-full">
                        <i class="ri-close-line"></i> Fermer
                    </button>
                    <button @click="printcodebarre" type="button" class="ti-btn ti-btn-primary-full" :disabled="skus.length === 0">
                        <i class="ri-printer-line"></i> Imprimer
                    </button>
                </footer>
            </div>
        </div>
    </transition>
</template>

<script>
    import { impression, randomString } from "@/allfunction";
    import * as JwtService from "@/common/jwt.service";
    import QRCode from "qrcode";

    const axios = require('axios');

    export default {
        /* eslint-disable */
        name: "ModalCodeGenerator",

        data() {
            return {
                isdisplay: false,
                titlemodal: "",
                datatable: [],
                typedoc: "",
                messageerror: "",
                messagesucces: "",
                taillemodel: 'A4',
                nombreimprimable: 30,
                titletaille: 10,
                codebarre: '',
                namecode: '',
                name: '',

                // Type de code
                typeCode: 'barcode',

                // Options QR Code
                qrSize: 80,
                qrColor: '#000000',

                // QR Code Data URL
                qrCodeDataUrl: '',

                // Options d'affichage
                showPrice: false,
                priceValue: 0,
                currency: 'FCFA',
                showBorder: true,
                marginSize: 2,

                // Toggle pour le format d'étiquettes
                layoutSectionVisible: false,

                // Config panel
                configExpanded: false,

                // Catégorie active
                activeCategory: 'retail',

                // Layout sélectionné
                selectedLayoutId: 'etibox-40',

                // Catégories de layouts
                layoutCategories: [
                    { id: 'pharma', name: 'Pharmacie', icon: '💊' },
                    { id: 'retail', name: 'Commerce', icon: '🏪' },
                    { id: 'jewelry', name: 'Bijouterie', icon: '💎' },
                    { id: 'logistics', name: 'Logistique', icon: '📦' },
                    { id: 'food', name: 'Alimentaire', icon: '🍎' },
                    { id: 'custom', name: 'Personnalisé', icon: '⚙️' }
                ],

                // Tous les layouts disponibles
                layouts: [
                    // PHARMACIE
                    {
                        id: 'pharma-3x11',
                        category: 'pharma',
                        name: 'Pharmacie Standard',
                        cols: 3,
                        rows: 11,
                        paperSize: 'A4',
                        labelWidth: 70,
                        labelHeight: 25,
                        gap: 0,
                        cardClass: 'card-pharma',
                        description: 'Format classique pour médicaments'
                    },
                    {
                        id: 'pharma-4x12',
                        category: 'pharma',
                        name: 'Pharmacie Compact',
                        cols: 4,
                        rows: 12,
                        paperSize: 'A4',
                        labelWidth: 52,
                        labelHeight: 22,
                        gap: 0,
                        cardClass: 'card-pharma-small',
                        description: 'Petites étiquettes médicaments'
                    },
                    {
                        id: 'pharma-2x7',
                        category: 'pharma',
                        name: 'Pharmacie Large',
                        cols: 2,
                        rows: 7,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 38,
                        gap: 0,
                        cardClass: 'card-pharma-large',
                        description: 'Grandes étiquettes officine'
                    },
                    {
                        id: 'pharma-shelf',
                        category: 'pharma',
                        name: 'Étiquette Rayon',
                        cols: 2,
                        rows: 5,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 50,
                        gap: 2,
                        cardClass: 'card-shelf',
                        description: 'Pour rayonnage pharmacie'
                    },

                    // COMMERCE / RETAIL
                    {
                        id: 'etibox-40',
                        category: 'retail',
                        name: 'Etibox 40 (52,5×29,7)',
                        cols: 4,
                        rows: 10,
                        paperSize: 'A4',
                        labelWidth: 52.5,
                        labelHeight: 29.7,
                        gap: 0,
                        // Calage précis sur planche pré-découpée (bord à bord, sans marge ni gouttière).
                        // Ajustez ces valeurs (mm) si un test d'impression révèle un léger décalage.
                        exactFit: true,
                        marginTop: 0,
                        marginLeft: 0,
                        columnGap: 0,
                        rowGap: 0,
                        cardClass: 'card-etibox',
                        description: 'Planche Etibox réf. 119769 - 40 étiquettes'
                    },
                    {
                        id: 'fabrisa-4',
                        category: 'retail',
                        name: 'Fabrisa A5 (105×148,5)',
                        cols: 2,
                        rows: 2,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 148.5,
                        gap: 0,
                        // Calage précis sur planche pré-découpée (bord à bord, sans marge ni gouttière).
                        // Ajustez ces valeurs (mm) si un test d'impression révèle un léger décalage.
                        exactFit: true,
                        marginTop: 0,
                        marginLeft: 0,
                        columnGap: 0,
                        rowGap: 0,
                        cardClass: 'card-fabrisa',
                        description: 'Planche Fabrisa réf. 15320 - 4 étiquettes A5'
                    },
                    {
                        id: 'fabrisa-6',
                        category: 'retail',
                        name: 'Fabrisa 6 (99,1×93,1)',
                        cols: 2,
                        rows: 3,
                        paperSize: 'A4',
                        labelWidth: 99.1,
                        labelHeight: 93.1,
                        gap: 0,
                        // Format standard Avery L7166 : vraies marges + gouttière horizontale.
                        // Ajustez ces valeurs (mm) si un test d'impression révèle un léger décalage.
                        exactFit: true,
                        marginTop: 8.85,
                        marginLeft: 4.65,
                        columnGap: 2.5,
                        rowGap: 0,
                        cardClass: 'card-fabrisa6',
                        description: 'Planche Fabrisa réf. 15330 - 6 étiquettes 99,1×93,1'
                    },
                    {
                        id: 'retail-3x10',
                        category: 'retail',
                        name: 'Commerce Standard',
                        cols: 3,
                        rows: 10,
                        paperSize: 'A4',
                        labelWidth: 70,
                        labelHeight: 29,
                        gap: 0,
                        cardClass: 'card-retail',
                        description: 'Format universel commerce'
                    },
                    {
                        id: 'retail-2x5',
                        category: 'retail',
                        name: 'Prix Gondole',
                        cols: 2,
                        rows: 5,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 57,
                        gap: 0,
                        cardClass: 'card-gondola',
                        description: 'Étiquettes rayonnage'
                    },
                    {
                        id: 'retail-4x11',
                        category: 'retail',
                        name: 'Mini Étiquette',
                        cols: 4,
                        rows: 11,
                        paperSize: 'A4',
                        labelWidth: 52,
                        labelHeight: 25,
                        gap: 0,
                        cardClass: 'card-mini',
                        description: 'Petits articles'
                    },
                    {
                        id: 'retail-1x4',
                        category: 'retail',
                        name: 'Grande Étiquette',
                        cols: 1,
                        rows: 4,
                        paperSize: 'A4',
                        labelWidth: 210,
                        labelHeight: 70,
                        gap: 2,
                        cardClass: 'card-large',
                        description: 'Articles volumineux'
                    },
                    {
                        id: 'retail-price',
                        category: 'retail',
                        name: 'Étiquette Prix',
                        cols: 3,
                        rows: 8,
                        paperSize: 'A4',
                        labelWidth: 70,
                        labelHeight: 35,
                        gap: 0,
                        cardClass: 'card-price',
                        description: 'Avec emplacement prix'
                    },

                    // BIJOUTERIE
                    {
                        id: 'jewelry-barbell',
                        category: 'jewelry',
                        name: 'Bijouterie Haltère',
                        cols: 3,
                        rows: 8,
                        paperSize: 'A4',
                        labelWidth: 70,
                        labelHeight: 12,
                        gap: 2,
                        cardClass: 'card-barbell',
                        description: 'Format haltère pour bagues'
                    },
                    {
                        id: 'jewelry-butterfly',
                        category: 'jewelry',
                        name: 'Bijouterie Papillon',
                        cols: 4,
                        rows: 10,
                        paperSize: 'A4',
                        labelWidth: 52,
                        labelHeight: 18,
                        gap: 1,
                        cardClass: 'card-butterfly',
                        description: 'Étiquette pliable bijoux'
                    },
                    {
                        id: 'jewelry-rectangle',
                        category: 'jewelry',
                        name: 'Bijouterie Rectangle',
                        cols: 5,
                        rows: 14,
                        paperSize: 'A4',
                        labelWidth: 40,
                        labelHeight: 20,
                        gap: 0,
                        cardClass: 'card-jewelry-rect',
                        description: 'Petite étiquette bijoux'
                    },
                    {
                        id: 'jewelry-string',
                        category: 'jewelry',
                        name: 'Étiquette Fil',
                        cols: 4,
                        rows: 12,
                        paperSize: 'A4',
                        labelWidth: 48,
                        labelHeight: 22,
                        gap: 1,
                        cardClass: 'card-string',
                        description: 'Avec trou pour fil'
                    },

                    // LOGISTIQUE
                    {
                        id: 'logistics-shipping',
                        category: 'logistics',
                        name: 'Étiquette Expédition',
                        cols: 1,
                        rows: 2,
                        paperSize: 'A4',
                        labelWidth: 210,
                        labelHeight: 148,
                        gap: 0,
                        cardClass: 'card-shipping',
                        description: 'Colis et palettes'
                    },
                    {
                        id: 'logistics-warehouse',
                        category: 'logistics',
                        name: 'Entrepôt Standard',
                        cols: 2,
                        rows: 4,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 70,
                        gap: 0,
                        cardClass: 'card-warehouse',
                        description: 'Stockage et inventaire'
                    },
                    {
                        id: 'logistics-pallet',
                        category: 'logistics',
                        name: 'Palette/Carton',
                        cols: 1,
                        rows: 3,
                        paperSize: 'A4',
                        labelWidth: 210,
                        labelHeight: 99,
                        gap: 0,
                        cardClass: 'card-pallet',
                        description: 'Grandes surfaces'
                    },
                    {
                        id: 'logistics-rack',
                        category: 'logistics',
                        name: 'Étiquette Rack',
                        cols: 2,
                        rows: 6,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 48,
                        gap: 0,
                        cardClass: 'card-rack',
                        description: 'Rayonnage entrepôt'
                    },

                    // ALIMENTAIRE
                    {
                        id: 'food-deli',
                        category: 'food',
                        name: 'Charcuterie/Traiteur',
                        cols: 2,
                        rows: 5,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 57,
                        gap: 0,
                        cardClass: 'card-deli',
                        description: 'Produits frais pesés'
                    },
                    {
                        id: 'food-bakery',
                        category: 'food',
                        name: 'Boulangerie',
                        cols: 3,
                        rows: 7,
                        paperSize: 'A4',
                        labelWidth: 70,
                        labelHeight: 40,
                        gap: 0,
                        cardClass: 'card-bakery',
                        description: 'Pains et viennoiseries'
                    },
                    {
                        id: 'food-produce',
                        category: 'food',
                        name: 'Fruits & Légumes',
                        cols: 2,
                        rows: 4,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 70,
                        gap: 0,
                        cardClass: 'card-produce',
                        description: 'Produits au kilo'
                    },
                    {
                        id: 'food-bottle',
                        category: 'food',
                        name: 'Bouteilles/Flacons',
                        cols: 3,
                        rows: 9,
                        paperSize: 'A4',
                        labelWidth: 70,
                        labelHeight: 30,
                        gap: 0,
                        cardClass: 'card-bottle',
                        description: 'Étiquette arrondie'
                    },

                    // PERSONNALISÉ
                    {
                        id: 'custom-2x4',
                        category: 'custom',
                        name: 'Personnalisé 2×4',
                        cols: 2,
                        rows: 4,
                        paperSize: 'A4',
                        labelWidth: 105,
                        labelHeight: 70,
                        gap: 2,
                        cardClass: 'card-custom',
                        description: '8 étiquettes par page'
                    },
                    {
                        id: 'custom-3x6',
                        category: 'custom',
                        name: 'Personnalisé 3×6',
                        cols: 3,
                        rows: 6,
                        paperSize: 'A4',
                        labelWidth: 70,
                        labelHeight: 48,
                        gap: 2,
                        cardClass: 'card-custom',
                        description: '18 étiquettes par page'
                    },
                    {
                        id: 'custom-4x8',
                        category: 'custom',
                        name: 'Personnalisé 4×8',
                        cols: 4,
                        rows: 8,
                        paperSize: 'A4',
                        labelWidth: 52,
                        labelHeight: 35,
                        gap: 2,
                        cardClass: 'card-custom',
                        description: '32 étiquettes par page'
                    },
                    {
                        id: 'custom-5x10',
                        category: 'custom',
                        name: 'Personnalisé 5×10',
                        cols: 5,
                        rows: 10,
                        paperSize: 'A4',
                        labelWidth: 40,
                        labelHeight: 28,
                        gap: 1,
                        cardClass: 'card-custom-small',
                        description: '50 étiquettes par page'
                    },
                    {
                        id: 'custom-thermal',
                        category: 'custom',
                        name: 'Thermique 40×25',
                        cols: 5,
                        rows: 11,
                        paperSize: 'A4',
                        labelWidth: 40,
                        labelHeight: 25,
                        gap: 0,
                        cardClass: 'card-thermal',
                        description: 'Compatible imprimante thermique'
                    },
                    {
                        id: 'custom-thermal-large',
                        category: 'custom',
                        name: 'Thermique 100×50',
                        cols: 2,
                        rows: 5,
                        paperSize: 'A4',
                        labelWidth: 100,
                        labelHeight: 50,
                        gap: 2,
                        cardClass: 'card-thermal-large',
                        description: 'Grande étiquette thermique'
                    }
                ],

                // Autres données
                searchword: "",
                searchtrierletableau: "",
                posts: [''],
                page: 1,
                perPage: 50,
                Defaultpage: 1,
                DefaultperPage: 50,
                pages: []
            };
        },

        computed: {
            filteredLayouts() {
                return this.layouts.filter(l => l.category === this.activeCategory);
            },

            selectedLayout() {
                return this.layouts.find(l => l.id === this.selectedLayoutId) || this.layouts[0];
            },

            skus() {
                let mvalue = [];
                let nombreimprimable = parseInt(this.nombreimprimable) || 0;

                for (let i = 0; i < nombreimprimable; i++) {
                    mvalue.push({
                        format: this.typeCode === 'barcode' ? 'code128' : 'qrcode',
                        title: this.namecode,
                        value: this.codebarre
                    });
                }

                return mvalue;
            },

            displayedPages() {
                const start = Math.max(1, this.page - 5);
                const end = Math.min(this.pages.length, start + 9);

                let result = [];

                if (start > 1) {
                    result.push(1);
                    if (start > 2) {
                        result.push('...');
                    }
                }

                for (let i = start; i <= end; i++) {
                    result.push(i);
                }

                if (end < this.pages.length) {
                    if (end < this.pages.length - 1) {
                        result.push('...');
                    }
                    result.push(this.pages.length);
                }

                return result;
            },

            filterdatatable() {
                let mvalue = this.datatable;
                this.perPage = this.DefaultperPage;

                if (this.searchword !== '') {
                    let t = this;
                    mvalue = mvalue.filter(function(post) {
                        return post.name.toLowerCase().includes(t.searchword.toLowerCase());
                    });
                }

                return mvalue;
            }
        },

        watch: {
            searchword: function(newvalue, oldvalue) {
                this.page = 1;
            },
            searchtrierletableau: function(newvalue, oldvalue) {
                this.page = 1;
            },
            activeCategory: function(newvalue) {
                // Sélectionner le premier layout de la nouvelle catégorie
                const firstLayout = this.layouts.find(l => l.category === newvalue);
                if (firstLayout) {
                    this.selectedLayoutId = firstLayout.id;
                }
            },
            // Watchers pour régénérer le QR code quand les paramètres changent
            namecode: {
                handler() {
                    this.generateQrCodeDataUrl();
                },
                immediate: true
            },
            qrSize() {
                this.generateQrCodeDataUrl();
            },
            qrColor() {
                this.generateQrCodeDataUrl();
            },
            typeCode() {
                if (this.typeCode === 'qrcode') {
                    this.generateQrCodeDataUrl();
                }
            }
        },

        methods: {
            close() {
                this.isdisplay = false;
            },

            incrementQuantity() {
                if (this.nombreimprimable < 500) {
                    this.nombreimprimable++;
                }
            },

            decrementQuantity() {
                if (this.nombreimprimable > 1) {
                    this.nombreimprimable--;
                }
            },

            validateQuantity() {
                if (this.nombreimprimable < 1) this.nombreimprimable = 1;
                if (this.nombreimprimable > 500) this.nombreimprimable = 500;
            },

            validatePrice() {
                if (this.priceValue < 0) this.priceValue = 0;
            },

            selectLayout(layout) {
                this.selectedLayoutId = layout.id;
                // Ajuster la quantité par défaut selon le layout
                const defaultQty = layout.cols * layout.rows;
                if (this.nombreimprimable === 0 || this.nombreimprimable > defaultQty * 3) {
                    this.nombreimprimable = defaultQty;
                }
            },

            toggleLayoutSection() {
                this.layoutSectionVisible = !this.layoutSectionVisible;
            },

            getLayoutPreviewStyle(layout) {
                return {
                    gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
                    gridTemplateRows: `repeat(${Math.min(layout.rows, 4)}, 1fr)`,
                    gap: '2px'
                };
            },

            getPreviewCellStyle(layout) {
                const aspectRatio = layout.labelWidth / layout.labelHeight;
                return {
                    aspectRatio: aspectRatio > 2 ? '2/1' : aspectRatio < 0.5 ? '1/2' : '1/1'
                };
            },

            getGridStyle() {
                const layout = this.selectedLayout;

                // Calage millimétré pour planches pré-découpées : colonnes/lignes
                // en mm fixes, marges et gouttières exactes issues du layout.
                if (layout.exactFit) {
                    return {
                        display: 'grid',
                        gridTemplateColumns: `repeat(${layout.cols}, ${layout.labelWidth}mm)`,
                        gridAutoRows: `${layout.labelHeight}mm`,
                        columnGap: `${layout.columnGap || 0}mm`,
                        rowGap: `${layout.rowGap || 0}mm`,
                        padding: `${layout.marginTop || 0}mm 0 0 ${layout.marginLeft || 0}mm`,
                        boxSizing: 'border-box'
                    };
                }

                return {
                    display: 'grid',
                    gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
                    gap: `${layout.gap || 0}mm`,
                    padding: `${this.marginSize}mm`
                };
            },

            getCardStyle() {
                const layout = this.selectedLayout;

                if (layout.exactFit) {
                    return {
                        width: `${layout.labelWidth}mm`,
                        height: `${layout.labelHeight}mm`,
                        padding: '0.5mm',
                        boxSizing: 'border-box',
                        overflow: 'hidden'
                    };
                }

                return {
                    minHeight: `${layout.labelHeight}mm`,
                    padding: `${Math.max(1, this.marginSize / 2)}mm`
                };
            },

            getBarcodeUrl(code) {
                const layout = this.selectedLayout;
                const height = Math.min(50, layout.labelHeight * 0.6);
                return `https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(code)}&code=Code128&translate-esc=true&height=${height}`;
            },

            getBarcodeStyle() {
                const layout = this.selectedLayout;
                return {
                    maxWidth: `${layout.labelWidth * 0.9}mm`,
                    maxHeight: `${layout.labelHeight * 0.4}mm`
                };
            },

            // Méthode pour générer le QR code en Data URL
            async generateQrCodeDataUrl() {
                if (!this.namecode) {
                    this.qrCodeDataUrl = '';
                    return;
                }

                try {
                    const url = await QRCode.toDataURL(this.namecode, {
                        width: this.qrSize,
                        margin: 2,
                        color: {
                            dark: this.qrColor,
                            light: '#ffffff'
                        },
                        errorCorrectionLevel: 'M'
                    });
                    this.qrCodeDataUrl = url;
                } catch (err) {
                    console.error('Erreur génération QR Code:', err);
                    this.qrCodeDataUrl = '';
                }
            },

            // Style pour le QR code image
            getQrCodeStyle() {
                return {
                    width: `${this.qrSize}px`,
                    height: `${this.qrSize}px`,
                    display: 'block',
                    margin: '0 auto'
                };
            },

            // Méthode pour générer un QR code pour impression POS
            async getQrcodeUrlPOS(code, paperSize = '48mm', color = '#000000') {
                const qrSize = this.getQrSizeByPaper(paperSize);

                return new Promise((resolve, reject) => {
                    QRCode.toDataURL(
                        code,
                        {
                            width: qrSize,
                            margin: 1,
                            color: {
                                dark: color,
                                light: '#ffffff'
                            }
                        },
                        (err, url) => {
                            if (err) {
                                console.error('QR ERROR', err);
                                reject(err);
                            } else {
                                resolve(url);
                            }
                        }
                    );
                });
            },

            getQrSizeByPaper(paperSize) {
                switch (paperSize) {
                    case '48mm':
                        return 220;
                    case '58mm':
                        return 260;
                    case '80mm':
                        return 320;
                    default:
                        return 220;
                }
            },

            formatPrice(price) {
                return new Intl.NumberFormat('fr-FR', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2
                }).format(price);
            },

            handleBarcodeError(event) {
                console.error('Erreur de chargement du code-barres');
                event.target.style.display = 'none';
            },

            handleQrCodeError(event) {
                console.error('Erreur de chargement du QR code');
                this.generateQrCodeDataUrl();
            },

            show(titlemodal, codebarre) {
                this.messagesucces = "";
                this.messageerror = "";

                this.name = "Générer le code barre pour " + titlemodal;
                this.codebarre = codebarre;
                this.namecode = titlemodal;
                this.titlemodal = "Générer le code barre pour " + titlemodal;

                // Reset to defaults
                this.nombreimprimable = 40;
                this.titletaille = 10;
                this.typeCode = 'barcode';
                this.qrSize = 80;
                this.qrColor = '#000000';
                this.showPrice = false;
                this.priceValue = 0;
                this.showBorder = true;
                this.marginSize = 2;
                this.configExpanded = false;
                this.activeCategory = 'retail';
                this.selectedLayoutId = 'etibox-40';
                this.layoutSectionVisible = false;

                // Générer le QR code initial
                //this.generateQrCodeDataUrl();

                this.isdisplay = true;
            },

            async loadreturnotherdata() {
                const t = this;
                let url = process.env.VUE_APP_API_URL + '/ar85474524otherdatadofodfidfoioienf55485/' + this.$store.state.appsociete + '/' + randomString();

                try {
                    const res = await axios.get(url, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + JwtService.getToken(),
                            'csrftoken': this.$store.state.appsociete
                        },
                        withCredentials: true
                    });

                    t.$store.dispatch('lancerchargement', false);

                    if (res.data.success) {
                        if (t.typedoc === 'cat') {
                            t.datatable = res.data.otherdata.catalogue || [];
                        }
                        if (t.typedoc === 'unite') {
                            t.datatable = res.data.otherdata.unite || [];
                        }
                        if (t.typedoc === 'emballage') {
                            t.datatable = res.data.otherdata.emballage || [];
                        }
                    } else {
                        t.datatable = [];
                    }
                } catch (error) {
                    t.$store.dispatch('lancerchargement', false);
                } finally {
                    t.$store.dispatch('lancerchargement', false);
                }
            },

            setPages(posts) {
                this.pages = [];
                let numberOfPages = Math.ceil(posts.length / this.perPage);
                for (let index = 1; index <= numberOfPages; index++) {
                    this.pages.push(index);
                }
            },

            paginate(posts) {
                this.setPages(posts);
                let page = this.page;
                let perPage = this.perPage;
                let from = (page * perPage) - perPage;
                let to = (page * perPage);

                let $res = posts.slice(from, to);
                return $res.length > 0 ? $res : posts;
            },


            async printcodebarre () {


                let t=this
                let $style=[],$scripts=[];

                let options = {  name: '_blank', }


                if (this.taillemodel==="A4landscape" || this.taillemodel==="A5landscape" ){

                    $style= [
                        process.env.VUE_APP_URL+'print/stylelandscape.css',

                    ]


                }

                if (this.taillemodel==="A4" || this.taillemodel==="A5" ){


                    $style= [
                          process.env.VUE_APP_URL+'print/styleportrait.css',
                    ]

                }

                $style= [
                    process.env.VUE_APP_URL+'print/printcodebarre.css',
                ]

                // Calage millimétré (planche pré-découpée) : force @page A4 sans marge,
                // body sans marge et hauteurs de cellule fixes. Chargé uniquement pour
                // les formats exactFit afin de ne pas impacter les autres layouts.
                if (this.selectedLayout && this.selectedLayout.exactFit) {
                    $style.push(process.env.VUE_APP_URL+'print/printcodebarre-exact.css');
                }


                $scripts= [
                    "https://cdn.tailwindcss.com",
                ]


                setTimeout(() => {
                    const printContent = this.$refs.printcodebarrep.innerHTML;
                    impression(printContent,$style,window.document.title,$scripts,{ format: this.taillemodel })

                }, 500);

            },



            async printcodebarre_old() {
                let $style = [];

                if (this.taillemodel === "A4landscape") {
                    $style = [
                        'https://cdn.jsdelivr.net/npm/bootstrap@4.0.0/dist/css/bootstrap.min.css',
                        process.env.VUE_APP_URL + '/stylecodebarre.css',
                        process.env.VUE_APP_URL + '/landscape.css'
                    ];
                } else {
                    $style = [
                        'https://cdn.jsdelivr.net/npm/bootstrap@4.0.0/dist/css/bootstrap.min.css',
                        process.env.VUE_APP_URL + '/stylecodebarre.css',
                        process.env.VUE_APP_URL + '/portrait.css'
                    ];
                }

                setTimeout(() => {
                    const printContent = this.$refs.printcodebarrep.innerHTML;
                    impression(printContent,$style,window.document.title,[],{ format: this.taillemodel });
                }, 500);
            },


        }
    };
</script>

<style scoped>
    /* ========================================
       Gabarit charte cf-dialog (en-tête + pied)
       ======================================== */
    .cf-dialog-head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 1rem 1.25rem; border-bottom: 1px solid var(--cf-border); flex-shrink: 0;
    }
    .cf-dialog-title { margin: 0; font-size: 0.95rem; font-weight: 700; color: var(--cf-ink-strong); }
    .cf-dialog-close {
        height: 2rem; width: 2rem; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
        border-radius: var(--cf-radius-sm); border: none; background: var(--cf-surface-alt); color: var(--cf-muted);
        cursor: pointer; font-size: 1.15rem; transition: all .15s;
    }
    .cf-dialog-close:hover { background: #fee2e2; color: rgb(var(--danger)); }
    .cf-dialog-foot {
        display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
        padding: 0.9rem 1.25rem; border-top: 1px solid var(--cf-border); background: var(--cf-surface); flex-shrink: 0;
    }

    /* ========================================
       Variables CSS - Design System
       ======================================== */
    .modal-overlay {
        /* Charte cf-/ds- : marque Ynex (#845ADF) + neutres cf (voir form.vue) */
        --primary: #845ADF;
        --primary-hover: #7349cc;
        --primary-light: #ece7fb;
        --primary-dark: #5b39b0;
        --secondary: #23b7e5;
        --secondary-light: #e3f6fc;
        --success: #26bf94;
        --success-hover: #1fa47f;
        --success-light: #d7f5ec;
        --danger: #e6533c;
        --danger-light: #fdecea;
        --warning: #f5b849;
        --warning-light: #fef3c7;
        --bg-primary: #ffffff;
        --bg-secondary: var(--cf-surface);
        --bg-tertiary: var(--cf-surface-alt);
        --bg-dark: #1e293b;
        --text-primary: var(--cf-ink-strong);
        --text-secondary: var(--cf-muted);
        --text-muted: var(--cf-faint);
        --border: var(--cf-border);
        --border-focus: #845ADF;
        --shadow: var(--cf-shadow-md);
        --shadow-sm: var(--cf-shadow-sm);
        --shadow-md: var(--cf-shadow-md);
        --radius: var(--cf-radius);
        --radius-md: var(--cf-radius-sm);
        --radius-sm: var(--cf-radius-sm);
        --radius-xs: 4px;
    }

    /* ========================================
       Modal Overlay & Container
       ======================================== */
    .modal-overlay {
        position: fixed;
        inset: 0;
        z-index: 1000;
        background: rgba(15, 23, 42, 0.75);
        backdrop-filter: blur(8px);
        display: flex;
        align-items: flex-end;
        justify-content: center;
        padding: 0;
    }

    @media (min-width: 768px) {
        .modal-overlay {
            align-items: center;
            padding: 1rem;
        }
    }

    .modal-container {
        background: var(--bg-primary);
        width: 100%;
        max-width: 100%;
        height: 100vh;
        max-height: 100vh;
        display: flex;
        flex-direction: column;
        border-radius: var(--radius) var(--radius) 0 0;
        box-shadow: var(--shadow);
        overflow: hidden;
        animation: slideUp 0.3s ease-out;
    }

    @media (min-width: 768px) {
        .modal-container {
            max-width: 100%;
            height: auto;
            max-height: 100vh;
            border-radius: var(--radius);
            animation: fadeScale 0.25s ease-out;
        }
    }

    @media (min-width: 1200px) {
        .modal-container {
            max-width: 100%;
        }
    }

    @keyframes slideUp {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
    }

    @keyframes fadeScale {
        from { transform: scale(0.95) translateY(20px); opacity: 0; }
        to { transform: scale(1) translateY(0); opacity: 1; }
    }

    /* ========================================
       Modal Header
       ======================================== */
    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 1.25rem;
       /* background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);*/
        background: #374151 !important; /* couleur gris foncé */
        background: linear-gradient(135deg, #4b5563 0%, #374151 100%) !important;
        flex-shrink: 0;
    }

    .header-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    .header-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        background: rgba(255, 255, 255, 0.2);
        border-radius: var(--radius-sm);
        color: white;
    }

    .header-icon svg {
        width: 1.25rem;
        height: 1.25rem;
    }

    .header-text {
        display: flex;
        flex-direction: column;
    }

    .modal-title {
        font-size: 0.9375rem;
        font-weight: 700;
        color: white;
        margin: 0;
        line-height: 1.3;
    }

    @media (min-width: 640px) {
        .modal-title {
            font-size: 1.0625rem;
        }
    }

    .modal-subtitle {
        font-size: 0.6875rem;
        color: rgba(255, 255, 255, 0.8);
        margin: 0.125rem 0 0;
        max-width: 150px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    @media (min-width: 640px) {
        .modal-subtitle {
            font-size: 0.75rem;
            max-width: 300px;
        }
    }

    .btn-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        border: none;
        background: rgba(255, 255, 255, 0.15);
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all 0.2s ease;
        color: white;
    }

    .btn-close:hover {
        background: rgba(255, 255, 255, 0.25);
    }

    .btn-close svg {
        width: 1.125rem;
        height: 1.125rem;
    }

    /* ========================================
       Type Selector
       ======================================== */
    .type-selector {
        display: flex;
        gap: 0.625rem;
        padding: 0.875rem 1.25rem;
        background: var(--bg-primary);
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
    }

    .type-btn {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 0.625rem;
        padding: 0.625rem 0.75rem;
        border: 2px solid var(--border);
        background: var(--bg-primary);
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
    }

    .type-btn:hover {
        border-color: var(--primary);
        background: var(--primary-light);
    }

    .type-btn.active {
        border-color: var(--primary);
        background: var(--primary-light);
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
    }

    .type-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        background: var(--bg-tertiary);
        border-radius: var(--radius-xs);
        color: var(--text-secondary);
        transition: all 0.2s ease;
        flex-shrink: 0;
    }

    .type-btn.active .type-icon {
        background: var(--primary);
        color: white;
    }

    .type-icon svg {
        width: 1.125rem;
        height: 1.125rem;
    }

    .type-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        min-width: 0;
    }

    .type-name {
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--text-primary);
    }

    .type-desc {
        font-size: 0.6875rem;
        color: var(--text-muted);
        display: none;
    }

    @media (min-width: 480px) {
        .type-desc {
            display: block;
        }
    }

    .type-check {
        position: absolute;
        top: -0.375rem;
        right: -0.375rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.25rem;
        height: 1.25rem;
        background: var(--primary);
        border-radius: 50%;
        color: white;
    }

    .type-check svg {
        width: 0.75rem;
        height: 0.75rem;
    }

    /* ========================================
       Layout Section
       ======================================== */
    .layout-section {
        padding: 0.875rem 1.25rem;
        background: var(--bg-secondary);
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
    }

    .layout-header {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .layout-header-top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .layout-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--text-primary);
        margin: 0;
    }

    .layout-title svg {
        width: 1rem;
        height: 1rem;
        color: var(--primary);
    }

    .layout-toggle-wrapper {
        display: flex;
        align-items: center;
    }

    .layout-toggle-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.375rem 0.75rem;
        background: var(--bg-tertiary);
        border: 1.5px solid var(--border);
        border-radius: 2rem;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--text-secondary);
    }

    .layout-toggle-btn:hover {
        border-color: var(--primary);
        color: var(--primary);
    }

    .layout-toggle-btn.active {
        background: var(--primary-light);
        border-color: var(--primary);
        color: var(--primary);
    }

    .toggle-track {
        position: relative;
        width: 1.75rem;
        height: 0.875rem;
        background: var(--text-muted);
        border-radius: 0.875rem;
        transition: all 0.2s ease;
    }

    .layout-toggle-btn.active .toggle-track {
        background: var(--success);
    }

    .toggle-thumb {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 0.625rem;
        height: 0.625rem;
        background: white;
        border-radius: 50%;
        transition: all 0.2s ease;
    }

    .layout-toggle-btn.active .toggle-thumb {
        left: calc(100% - 0.625rem - 2px);
    }

    .toggle-label {
        font-size: 0.75rem;
        font-weight: 600;
    }

    .layout-selected {
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--primary);
        background: var(--primary-light);
        padding: 0.25rem 0.625rem;
        border-radius: 2rem;
        align-self: flex-start;
    }

    .layout-content {
        overflow: hidden;
    }

    .layout-slide-enter-active,
    .layout-slide-leave-active {
        transition: all 0.25s ease;
        max-height: 200px;
    }

    .layout-slide-enter-from,
    .layout-slide-leave-to {
        opacity: 0;
        max-height: 0;
    }

    /* Categories */
    .layout-categories {
        display: flex;
        gap: 0.375rem;
        margin-bottom: 0.75rem;
        overflow-x: auto;
        padding-bottom: 0.25rem;
        scrollbar-width: none;
    }

    .layout-categories::-webkit-scrollbar {
        display: none;
    }

    .category-btn {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.375rem 0.75rem;
        border: 1px solid var(--border);
        background: var(--bg-primary);
        border-radius: 2rem;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
        font-size: 0.75rem;
        color: var(--text-secondary);
    }

    .category-btn:hover {
        border-color: var(--primary);
        color: var(--primary);
    }

    .category-btn.active {
        background: var(--primary);
        border-color: var(--primary);
        color: white;
    }

    .category-icon {
        font-size: 0.875rem;
    }

    .category-name {
        font-weight: 500;
    }

    /* Layout Grid */
    .layout-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.5rem;
        max-height: 140px;
        overflow-y: auto;
        padding-right: 0.25rem;
    }

    @media (min-width: 640px) {
        .layout-grid {
            grid-template-columns: repeat(3, 1fr);
            max-height: 160px;
        }
    }

    @media (min-width: 900px) {
        .layout-grid {
            grid-template-columns: repeat(4, 1fr);
        }
    }

    .layout-card {
        display: flex;
        align-items: center;
        gap: 0.625rem;
        padding: 0.5rem;
        border: 2px solid var(--border);
        background: var(--bg-primary);
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
        text-align: left;
    }

    .layout-card:hover {
        border-color: var(--primary);
        background: var(--primary-light);
    }

    .layout-card.active {
        border-color: var(--primary);
        background: var(--primary-light);
    }

    .layout-preview {
        display: grid;
        width: 2.5rem;
        height: 2.5rem;
        background: var(--bg-tertiary);
        border-radius: var(--radius-xs);
        padding: 3px;
        flex-shrink: 0;
    }

    .preview-cell {
        background: var(--text-muted);
        border-radius: 1px;
        opacity: 0.4;
    }

    .layout-card.active .preview-cell {
        background: var(--primary);
        opacity: 0.7;
    }

    .layout-info {
        display: flex;
        flex-direction: column;
        min-width: 0;
        flex: 1;
    }

    .layout-name {
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .layout-dimensions {
        font-size: 0.5625rem;
        color: var(--text-muted);
    }

    .layout-check {
        position: absolute;
        top: -0.25rem;
        right: -0.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1rem;
        height: 1rem;
        background: var(--primary);
        border-radius: 50%;
        color: white;
    }

    .layout-check svg {
        width: 0.625rem;
        height: 0.625rem;
    }

    /* ========================================
       Configuration Panel
       ======================================== */
    .config-panel {
        background: var(--bg-tertiary);
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
    }

    .config-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.625rem 1.25rem;
        cursor: pointer;
        user-select: none;
    }

    .config-header:hover {
        background: rgba(0,0,0,0.02);
    }

    .config-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--text-primary);
        margin: 0;
    }

    .config-title svg {
        width: 1rem;
        height: 1rem;
        color: var(--primary);
    }

    .config-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.5rem;
        height: 1.5rem;
        border: none;
        background: transparent;
        border-radius: var(--radius-xs);
        color: var(--text-muted);
        transition: all 0.2s ease;
    }

    .config-toggle svg {
        width: 1rem;
        height: 1rem;
        transition: transform 0.2s ease;
    }

    .config-toggle.expanded svg {
        transform: rotate(180deg);
    }

    .config-content {
        padding: 0 1.25rem 0.875rem;
    }

    .config-slide-enter-active,
    .config-slide-leave-active {
        transition: all 0.25s ease;
        overflow: hidden;
    }

    .config-slide-enter-from,
    .config-slide-leave-to {
        opacity: 0;
        max-height: 0;
    }

    .config-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.75rem;
    }

    @media (min-width: 640px) {
        .config-grid {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    @media (min-width: 900px) {
        .config-grid {
            grid-template-columns: repeat(4, 1fr);
        }
    }

    .config-item {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
    }

    .config-label {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--text-secondary);
        text-transform: uppercase;
        letter-spacing: 0.02em;
    }

    .config-label svg {
        width: 0.75rem;
        height: 0.75rem;
        color: var(--text-muted);
    }

    /* Toggle Group */
    .toggle-group {
        display: flex;
        gap: 0.375rem;
    }

    .toggle-btn {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.125rem;
        padding: 0.375rem;
        border: 1.5px solid var(--border);
        background: var(--bg-primary);
        border-radius: var(--radius-xs);
        cursor: pointer;
        transition: all 0.2s ease;
        color: var(--text-secondary);
    }

    .toggle-btn:hover {
        border-color: var(--primary);
        color: var(--primary);
    }

    .toggle-btn.active {
        border-color: var(--primary);
        background: var(--primary-light);
        color: var(--primary);
    }

    .toggle-text {
        font-size: 0.5625rem;
        font-weight: 600;
        text-transform: uppercase;
    }

    .orientation-icon {
        width: 1rem;
        height: 1rem;
    }

    /* Number Input */
    .number-input-wrapper {
        display: flex;
        align-items: stretch;
        border: 1.5px solid var(--border);
        border-radius: var(--radius-xs);
        overflow: hidden;
        background: var(--bg-primary);
    }

    .number-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        border: none;
        background: var(--bg-tertiary);
        cursor: pointer;
        transition: all 0.2s ease;
        color: var(--text-secondary);
    }

    .number-btn:hover:not(:disabled) {
        background: var(--primary-light);
        color: var(--primary);
    }

    .number-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .number-btn svg {
        width: 0.875rem;
        height: 0.875rem;
    }

    .number-input {
        flex: 1;
        min-width: 0;
        padding: 0.375rem;
        border: none;
        text-align: center;
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--text-primary);
        background: transparent;
        outline: none;
    }

    .number-input::-webkit-inner-spin-button,
    .number-input::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Slider */
    .slider-wrapper {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.375rem 0.5rem;
        background: var(--bg-primary);
        border: 1.5px solid var(--border);
        border-radius: var(--radius-xs);
    }

    .slider-input {
        flex: 1;
        height: 4px;
        -webkit-appearance: none;
        appearance: none;
        background: var(--bg-tertiary);
        border-radius: 2px;
        outline: none;
    }

    .slider-input::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 14px;
        height: 14px;
        background: var(--primary);
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 4px rgba(99, 102, 241, 0.3);
    }

    .slider-value {
        font-size: 0.6875rem;
        font-weight: 700;
        color: var(--primary);
        min-width: 36px;
        text-align: right;
    }

    /* Toggle Switch */
    .toggle-switch-wrapper {
        display: flex;
    }

    .toggle-switch {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.25rem;
        border: none;
        background: transparent;
        cursor: pointer;
    }

    .switch-track {
        position: relative;
        width: 2.25rem;
        height: 1.25rem;
        background: var(--bg-tertiary);
        border-radius: 1rem;
        border: 1.5px solid var(--border);
        transition: all 0.2s ease;
    }

    .toggle-switch.active .switch-track {
        background: var(--success);
        border-color: var(--success);
    }

    .switch-thumb {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 0.875rem;
        height: 0.875rem;
        background: white;
        border-radius: 50%;
        box-shadow: var(--shadow-sm);
        transition: all 0.2s ease;
    }

    .toggle-switch.active .switch-thumb {
        left: calc(100% - 1rem);
    }

    .switch-label {
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--text-secondary);
    }

    /* Price Input */
    .price-input-wrapper {
        display: flex;
        align-items: center;
        border: 1.5px solid var(--border);
        border-radius: var(--radius-xs);
        overflow: hidden;
        background: var(--bg-primary);
    }

    .price-input {
        flex: 1;
        min-width: 0;
        padding: 0.375rem 0.5rem;
        border: none;
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--text-primary);
        background: transparent;
        outline: none;
    }

    .price-currency {
        padding: 0.375rem 0.5rem;
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--text-muted);
        background: var(--bg-tertiary);
    }

    /* Select */
    .select-wrapper {
        position: relative;
    }

    .config-select {
        width: 100%;
        padding: 0.375rem 0.5rem;
        font-size: 0.75rem;
        border: 1.5px solid var(--border);
        border-radius: var(--radius-xs);
        background: var(--bg-primary);
        color: var(--text-primary);
        cursor: pointer;
        outline: none;
    }

    .config-select:focus {
        border-color: var(--primary);
    }

    /* Color Picker */
    .color-picker-wrapper {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.25rem 0.5rem;
        background: var(--bg-primary);
        border: 1.5px solid var(--border);
        border-radius: var(--radius-xs);
    }

    .color-input {
        width: 1.5rem;
        height: 1.5rem;
        border: none;
        border-radius: var(--radius-xs);
        cursor: pointer;
        padding: 0;
    }

    .color-input::-webkit-color-swatch-wrapper {
        padding: 0;
    }

    .color-input::-webkit-color-swatch {
        border: none;
        border-radius: var(--radius-xs);
    }

    .color-value {
        font-size: 0.6875rem;
        font-weight: 600;
        font-family: monospace;
        color: var(--text-secondary);
        text-transform: uppercase;
    }

    /* ========================================
       Modal Body (Preview)
       ======================================== */
    .modal-body {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        background: var(--bg-tertiary);
        padding: 0.875rem 1.25rem;
    }

    .preview-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.75rem;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .preview-title {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--text-primary);
        margin: 0;
    }

    .preview-title svg {
        width: 1rem;
        height: 1rem;
        color: var(--text-muted);
    }

    .preview-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.25rem 0.625rem;
        background: var(--bg-dark);
        border-radius: 2rem;
    }

    .badge-text {
        font-size: 0.6875rem;
        font-weight: 600;
        color: white;
        font-family: monospace;
    }

    .preview-container {
        background: var(--bg-primary);
        border-radius: var(--radius-md);
        border: 1px solid var(--border);
        overflow: hidden;
        min-height: 180px;
        max-height: 350px;
        overflow-y: auto;
    }

    /* ========================================
       Code Grid & Cards
       ======================================== */
    .code-grid {
        display: grid;
    }

    .code-card {
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
        transition: all 0.15s ease;
    }

    .code-card.with-border {
        border: 1px dashed var(--border);
    }

    .code-card:hover {
        background: var(--bg-secondary);
    }

    .code-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        text-align: center;
    }

    .code-label {
        margin: 0 0 0.25rem;
        font-weight: 600;
        color: var(--text-primary);
        line-height: 1.2;
        word-break: break-word;
    }

    .barcode-image {
        width: 100%;
        height: auto;
        max-height: 40px;
        object-fit: contain;
    }

    .qr-image {
        display: block;
        margin: 0 auto;
    }

    .code-number {
        margin: 0.25rem 0 0;
        font-size: 0.5625rem;
        font-family: monospace;
        color: var(--text-muted);
    }

    .code-price {
        margin: 0.25rem 0 0;
        font-size: 0.75rem;
        font-weight: 700;
        color: var(--danger);
    }

    /* ========================================
       Empty State
       ======================================== */
    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 2.5rem 1.5rem;
        text-align: center;
    }

    .empty-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3.5rem;
        height: 3.5rem;
        background: var(--bg-tertiary);
        border-radius: 50%;
        margin-bottom: 0.875rem;
        color: var(--text-muted);
    }

    .empty-icon svg {
        width: 1.75rem;
        height: 1.75rem;
    }

    .empty-text {
        font-size: 0.8125rem;
        color: var(--text-muted);
        margin: 0;
    }

    /* ========================================
       Modal Footer
       ======================================== */
    .modal-footer {
        display: flex;
        gap: 0.625rem;
        padding: 0.875rem 1.25rem;
        border-top: 1px solid var(--border);
        background: var(--bg-primary);
        flex-shrink: 0;
    }

    .btn {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        padding: 0.75rem 1rem;
        font-size: 0.875rem;
        font-weight: 600;
        border: none;
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .btn svg {
        width: 1.125rem;
        height: 1.125rem;
    }

    .btn-cancel {
        background: var(--bg-tertiary);
        color: var(--text-secondary);
    }

    .btn-cancel:hover {
        background: var(--danger-light);
        color: var(--danger);
    }

    .btn-test {
        background: linear-gradient(135deg, var(--warning) 0%, #d97706 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
    }

    .btn-test:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    }

    .btn-test:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }

    .btn-print {
        background: linear-gradient(135deg, var(--success) 0%, var(--success-hover) 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

    .btn-print:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
    }

    .btn-print:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }

    /* ========================================
       Animations & Transitions
       ======================================== */
    .modal-slide-enter-active,
    .modal-slide-leave-active {
        transition: all 0.3s ease;
    }

    .modal-slide-enter-from,
    .modal-slide-leave-to {
        opacity: 0;
    }

    .modal-slide-enter-from .modal-container,
    .modal-slide-leave-to .modal-container {
        transform: translateY(100%);
    }

    @media (min-width: 768px) {
        .modal-slide-enter-from .modal-container,
        .modal-slide-leave-to .modal-container {
            transform: scale(0.95) translateY(20px);
        }
    }

    /* ========================================
       Scrollbars
       ======================================== */
    .modal-body::-webkit-scrollbar,
    .preview-container::-webkit-scrollbar,
    .layout-grid::-webkit-scrollbar {
        width: 4px;
    }

    .modal-body::-webkit-scrollbar-track,
    .preview-container::-webkit-scrollbar-track,
    .layout-grid::-webkit-scrollbar-track {
        background: transparent;
    }

    .modal-body::-webkit-scrollbar-thumb,
    .preview-container::-webkit-scrollbar-thumb,
    .layout-grid::-webkit-scrollbar-thumb {
        background: var(--border);
        border-radius: 2px;
    }

    .modal-body::-webkit-scrollbar-thumb:hover,
    .preview-container::-webkit-scrollbar-thumb:hover,
    .layout-grid::-webkit-scrollbar-thumb:hover {
        background: var(--text-muted);
    }

    /* ========================================
       Responsive adjustments
       ======================================== */
    @media (max-width: 640px) {
        .modal-footer {
            flex-wrap: wrap;
        }

        .modal-footer .btn {
            flex: 1 1 calc(50% - 0.3125rem);
            min-width: 120px;
        }

        .layout-toggle-btn .toggle-label {
            display: none;
        }

        .layout-toggle-btn {
            padding: 0.375rem;
        }

        .config-grid {
            grid-template-columns: repeat(1, 1fr);
        }
    }

    /* ========================================
       Print Styles
       ======================================== */
    @media print {
        .modal-overlay {
            position: static;
            background: none;
        }

        .modal-header,
        .type-selector,
        .layout-section,
        .config-panel,
        .modal-footer,
        .preview-header {
            display: none !important;
        }

        .modal-container {
            max-width: 100%;
            height: auto;
            max-height: none;
            box-shadow: none;
            border-radius: 0;
        }

        .modal-body {
            padding: 0;
            overflow: visible;
        }

        .preview-container {
            border: none;
            border-radius: 0;
            max-height: none;
        }

        .code-card {
            break-inside: avoid;
        }
    }
</style>